import { router } from 'expo-router';
import { Linking } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    ArrowUpRightIcon,
    Button,
    ButtonSize,
    ButtonVariant,
    CONTENT_MAX_WIDTH,
    FileTextIcon,
    FolderOpenIcon,
    FRUITFUL_ICON_KEY,
    FruitfulCustomIcon,
    FullScreenSpinner,
    H6Emphasized,
    Icon,
    isWeb,
    MailIcon,
    NavigationRow,
    NextAction,
    Row,
    RowList,
    SCREEN_CONTAINER_HORIZONTAL_PADDING,
    ScreenContainer,
    useThemeColors,
    XStack,
    YStack,
} from 'smoothie';

import Logo from '../../../assets/images/logos/logo-new.svg';
import { useLogout } from '../../auth/hooks/useLogout';
import { LogoutType } from '../../auth/types/Auth';
import { useSendEmailToSupport } from '../../common/hooks/useSendEmailToSupport';
import { SUPPORT_EMAIL } from '../../settings/types/constants';
import { useFormerMemberPortal } from '../hooks/useFormerMemberPortal';

const FormerMemberHeader = () => {
    const { top } = useSafeAreaInsets();
    const { getColor } = useThemeColors();

    return (
        <XStack
            width="100%"
            paddingTop={isWeb ? 0 : top}
            paddingHorizontal={SCREEN_CONTAINER_HORIZONTAL_PADDING}
            justifyContent="center"
            backgroundColor="$white"
        >
            <XStack
                width="100%"
                maxWidth={CONTENT_MAX_WIDTH}
                paddingTop={20}
                paddingBottom={12}
                alignItems="center"
                justifyContent="flex-start"
            >
                <Logo height={24} color={getColor('$black')} />
            </XStack>
        </XStack>
    );
};

export const FormerMemberPortal = () => {
    const { logOut } = useLogout();
    const sendEmailToSupport = useSendEmailToSupport();
    const { data: portal, isLoading } = useFormerMemberPortal();
    const banking = portal?.banking ?? [];
    const investments = portal?.investments ?? [];

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    return (
        <YStack flex={1} backgroundColor="$white" testID="former-member-portal">
            <FormerMemberHeader />
            <ScreenContainer useSafeAreaInsetTop={false}>
                <YStack
                    gap={24}
                    width="100%"
                    $sm={{ maxWidth: 500, alignSelf: 'center' }}
                >
                    <NextAction
                        backgroundColor="$white"
                        shadow={true}
                        title="Your Fruitful membership has ended."
                        description="You still have access to statements and tax documents for any banking and investment accounts you previously held. If you're interested in rejoining, you can start a new membership whenever you're ready."
                        iconOverride={
                            <FruitfulCustomIcon
                                size="sm"
                                icon={FRUITFUL_ICON_KEY}
                            />
                        }
                        primaryAction={{
                            text: 'Get started',
                            onPress: () => {
                                const url = 'https://fruitful.com';
                                void Linking.openURL(url);
                            },
                        }}
                    />
                    {banking.length > 0 && (
                        <YStack gap={8}>
                            <H6Emphasized>Banking</H6Emphasized>
                            <RowList width="100%">
                                {banking.flatMap((bankingAccess) => [
                                    <NavigationRow
                                        key={`${bankingAccess.userId}-statements`}
                                        title="Statements"
                                        subtitle={bankingAccess.membershipLabel}
                                        leftIcon={FolderOpenIcon}
                                        onPress={() => {
                                            router.navigate({
                                                pathname:
                                                    '/former-member/banking/[userId]/statements',
                                                params: {
                                                    userId: bankingAccess.userId,
                                                },
                                            });
                                        }}
                                    />,
                                    <NavigationRow
                                        key={`${bankingAccess.userId}-tax`}
                                        title="Tax forms"
                                        subtitle={bankingAccess.membershipLabel}
                                        leftIcon={FileTextIcon}
                                        onPress={() => {
                                            router.navigate({
                                                pathname:
                                                    '/former-member/banking/[userId]/tax-forms',
                                                params: {
                                                    userId: bankingAccess.userId,
                                                },
                                            });
                                        }}
                                    />,
                                ])}
                            </RowList>
                        </YStack>
                    )}
                    {investments.length > 0 && (
                        <YStack gap={8}>
                            <H6Emphasized>Investments</H6Emphasized>
                            <RowList width="100%">
                                {investments.flatMap((account) => [
                                    <NavigationRow
                                        key={`${account.apexAccountId}-statements`}
                                        title={`${account.nickname} statements`}
                                        subtitle={account.membershipLabel}
                                        leftIcon={FolderOpenIcon}
                                        onPress={() => {
                                            router.navigate({
                                                pathname:
                                                    '/former-member/investments/[apexAccountId]/statements',
                                                params: {
                                                    apexAccountId:
                                                        account.apexAccountId,
                                                },
                                            });
                                        }}
                                    />,
                                    <NavigationRow
                                        key={`${account.apexAccountId}-tax`}
                                        title={`${account.nickname} tax forms`}
                                        subtitle={account.membershipLabel}
                                        leftIcon={FileTextIcon}
                                        onPress={() => {
                                            router.navigate({
                                                pathname:
                                                    '/former-member/investments/[apexAccountId]/tax-forms',
                                                params: {
                                                    apexAccountId:
                                                        account.apexAccountId,
                                                },
                                            });
                                        }}
                                    />,
                                ])}
                            </RowList>
                        </YStack>
                    )}
                    <RowList width="100%">
                        <Row
                            title="Contact Fruitful Hospitality"
                            subtitle={SUPPORT_EMAIL}
                            leftIcon={MailIcon}
                            onPress={() => {
                                void sendEmailToSupport();
                            }}
                            rightContent={<Icon icon={ArrowUpRightIcon} />}
                        />
                    </RowList>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Sign out"
                        onPress={() => {
                            void logOut(LogoutType.NoAccessScreenButton);
                        }}
                    />
                </YStack>
            </ScreenContainer>
        </YStack>
    );
};
